import React, { useState, useEffect, useCallback } from 'react';
import {
  fetchMaintenanceLogApi,
  fetchStoresApi,
  fetchRegionalCoachesApi,
  fetchAreaCoachesForMaintenanceApi,
  exportMaintenanceLogApi,
  MaintenanceLogItem,
  MaintenanceLogFilters,
  StoreApiResponse,
  RegionalCoachApiResponse,
  AreaCoachApiResponse,
} from '../../services/api';
import { useSelector } from 'react-redux';
import { RootState } from '../../app/store';
import type { AuthUser } from '../auth/authSlice';
import toast from 'react-hot-toast';
import MaintenanceDetailsModal from './MaintenanceDetailsModal';

type ViewMode = 'card' | 'table';

const FILTERS_STORAGE_KEY = 'maintenance_log_filters';

const getDefaultFilters = (): MaintenanceLogFilters => ({
  date_from: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
  date_to: new Date().toISOString().split('T')[0],
  status: 2,
});

interface PersistedFilters {
  filters: MaintenanceLogFilters;
  selectedStores: number[];
  selectedSuppliers: number[];
  selectedRegional: string;
  selectedAreaCoach: string;
}

const loadPersistedFilters = (): PersistedFilters | null => {
  try {
    const saved = localStorage.getItem(FILTERS_STORAGE_KEY);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (e) {
    console.error('Error loading persisted filters:', e);
  }
  return null;
};

const MaintenanceLogPage: React.FC = () => {
  const user = useSelector((state: RootState) => state.auth.user) as AuthUser | null;

  // View mode state
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const saved = localStorage.getItem('maintenance_view_mode');
    return (saved as ViewMode) || 'card';
  });

  // State for data
  const [requisitions, setRequisitions] = useState<MaintenanceLogItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    total: 0,
    per_page: 20,
    current_page: 1,
    last_page: 1,
  });

  // State for filter options
  const [stores, setStores] = useState<StoreApiResponse[]>([]);
  const [regionals, setRegionals] = useState<RegionalCoachApiResponse[]>([]);
  const [areaCoaches, setAreaCoaches] = useState<AreaCoachApiResponse[]>([]);

  // Load persisted filters on initial render
  const persistedFilters = loadPersistedFilters();
  const defaultFilters = getDefaultFilters();

  // State for filters
  const [filters, setFilters] = useState<MaintenanceLogFilters>(persistedFilters?.filters || defaultFilters);

  // State for selected items
  const [selectedStores, setSelectedStores] = useState<number[]>(persistedFilters?.selectedStores || []);
  const [selectedSuppliers, setSelectedSuppliers] = useState<number[]>(persistedFilters?.selectedSuppliers || []);
  const [selectedRegional, setSelectedRegional] = useState<string>(persistedFilters?.selectedRegional || '');
  const [selectedAreaCoach, setSelectedAreaCoach] = useState<string>(persistedFilters?.selectedAreaCoach || '');

  // Filter visibility states
  const [mobileFiltersVisible, setMobileFiltersVisible] = useState(false);
  const [desktopFiltersVisible, setDesktopFiltersVisible] = useState(false);

  // Modal state
  const [selectedRequisition, setSelectedRequisition] = useState<MaintenanceLogItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Handle view details
  const handleViewDetails = (requisition: MaintenanceLogItem) => {
    setSelectedRequisition(requisition);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedRequisition(null);
  };

  // Save view mode preference
  useEffect(() => {
    localStorage.setItem('maintenance_view_mode', viewMode);
  }, [viewMode]);

  // Save filters to localStorage whenever they change
  useEffect(() => {
    const data: PersistedFilters = {
      filters,
      selectedStores,
      selectedSuppliers,
      selectedRegional,
      selectedAreaCoach,
    };
    localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(data));
  }, [filters, selectedStores, selectedSuppliers, selectedRegional, selectedAreaCoach]);

  // Fetch filter options on mount
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const [storesData, regionalsData] = await Promise.all([
          fetchStoresApi(),
          fetchRegionalCoachesApi(),
        ]);
        setStores(storesData);
        setRegionals(regionalsData);

        // Set default filters based on user role only when no persisted filters exist
        const persisted = loadPersistedFilters();
        if (!persisted && user) {
          if (user.user_role_id === 2) {
            // Area Coach - pre-select themselves
            setSelectedAreaCoach(String(user.id));
            const userStores = storesData.filter((s: any) => s.area_id === user.id);
            setSelectedStores(userStores.map((s: any) => s.id));
          } else if (user.user_role_id === 3) {
            // Regional Coach - pre-select themselves and load their area coaches
            setSelectedRegional(String(user.id));
            const userStores = storesData.filter((s: any) => s.regional_id === user.id);
            setSelectedStores(userStores.map((s: any) => s.id));
            
            // Fetch area coaches for this regional coach
            try {
              const areaCoachesData = await fetchAreaCoachesForMaintenanceApi(String(user.id));
              setAreaCoaches(areaCoachesData);
            } catch (error) {
              console.error('Error fetching area coaches for regional:', error);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching filter options:', error);
        toast.error('Failed to load filter options');
      }
    };

    fetchFilterOptions();
  }, [user]);

  // Fetch area coaches when regional changes
  useEffect(() => {
    const fetchAreaCoaches = async () => {
      try {
        const data = await fetchAreaCoachesForMaintenanceApi(selectedRegional);
        setAreaCoaches(data);
      } catch (error) {
        console.error('Error fetching area coaches:', error);
      }
    };
    fetchAreaCoaches();
  }, [selectedRegional]);

  // Fetch maintenance log data
  const fetchMaintenanceLog = useCallback(
    async (page: number = 1) => {
      setLoading(true);
      try {
        const response = await fetchMaintenanceLogApi(
          {
            ...filters,
            selected_stores: selectedStores.length > 0 ? selectedStores : undefined,
            selected_suppliers: selectedSuppliers.length > 0 ? selectedSuppliers : undefined,
            selected_regional: selectedRegional || undefined,
            selected_area_coach: selectedAreaCoach || undefined,
          },
          page
        );
        setRequisitions(response.data);
        setPagination(response.pagination);
      } catch (error) {
        console.error('Error fetching maintenance log:', error);
        toast.error('Failed to load maintenance log data');
      } finally {
        setLoading(false);
      }
    },
    [filters, selectedStores, selectedSuppliers, selectedRegional, selectedAreaCoach]
  );

  // Initial fetch and refetch on filter changes
  useEffect(() => {
    fetchMaintenanceLog(1);
  }, [fetchMaintenanceLog]);

  // Handle filter changes
  const handleFilterChange = (key: keyof MaintenanceLogFilters, value: any) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  // Handle regional change
  const handleRegionalChange = (value: string) => {
    setSelectedRegional(value);
    setSelectedAreaCoach('');
    if (value) {
      const regionalStores = stores.filter((s: any) => s.regional_id === Number(value));
      setSelectedStores(regionalStores.map((s: any) => s.id));
    } else {
      setSelectedStores([]);
    }
  };

  // Handle area coach change
  const handleAreaCoachChange = (value: string) => {
    setSelectedAreaCoach(value);
    setSelectedRegional('');
    if (value) {
      const areaCoachStores = stores.filter((s: any) => s.area_id === Number(value));
      setSelectedStores(areaCoachStores.map((s: any) => s.id));
    } else {
      setSelectedStores([]);
    }
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters(getDefaultFilters());
    setSelectedStores([]);
    setSelectedSuppliers([]);
    setSelectedRegional('');
    setSelectedAreaCoach('');
    localStorage.removeItem(FILTERS_STORAGE_KEY);
  };

  // Export to Excel
  const handleExport = async () => {
    try {
      await exportMaintenanceLogApi({
        ...filters,
        selected_stores: selectedStores.length > 0 ? selectedStores : undefined,
        selected_suppliers: selectedSuppliers.length > 0 ? selectedSuppliers : undefined,
        selected_regional: selectedRegional || undefined,
        selected_area_coach: selectedAreaCoach || undefined,
      });
      toast.success('Export started successfully');
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error('Failed to export data');
    }
  };

  // Calculate completion status
  const getCompletionStatus = (requisition: MaintenanceLogItem): 'COMPLETED' | 'PENDING' => {
    const equipmentRequests = requisition.equipmentRequests || [];
    const assets = requisition.assets || [];

    const equipmentCompleted = equipmentRequests.filter((e) => e.completed).length;
    const totalEquipment = equipmentRequests.length;
    const assetsCompleted = assets.filter((a) => a.completed).length;
    const totalAssets = assets.length;

    const totalItems = totalEquipment + totalAssets;
    const totalCompleted = equipmentCompleted + assetsCompleted;

    return totalItems > 0 && totalCompleted === totalItems ? 'COMPLETED' : 'PENDING';
  };

  // Render filter section
  const renderFilters = () => (
    <div className="space-y-4">
      {/* Search */}
      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
          Search
        </label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg
              className="h-5 w-5 text-slate-400 dark:text-slate-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
          <input
            type="text"
            className="block w-full pl-10 pr-3 py-2.5 bg-white dark:bg-midnight-900/50 border border-slate-300 dark:border-white/10 rounded-lg text-slate-900 dark:text-slate-200 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-brand-400/50 focus:border-brand-400/50 transition-all text-sm"
            placeholder="Search by store, supplier, category..."
            value={filters.search || ''}
            onChange={(e) => handleFilterChange('search', e.target.value)}
          />
        </div>
      </div>

      {/* Regional and Area Coach Filters */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {(user?.is_admin || user?.user_role_id === 5 || user?.user_role_id === 3) && (
          <>
            {(user?.is_admin || user?.user_role_id === 5) && (
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                  Regional Coach
                </label>
                <select
                  className="block w-full px-3 py-2.5 bg-white dark:bg-midnight-900/50 border border-slate-300 dark:border-white/10 rounded-lg text-slate-900 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-brand-400/50 focus:border-brand-400/50 transition-all text-sm"
                  value={selectedRegional}
                  onChange={(e) => handleRegionalChange(e.target.value)}
                >
                  <option value="">All Regionals</option>
                  {regionals.map((regional) => (
                    <option key={regional.id} value={regional.id}>
                      {regional.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                Area Coach
              </label>
              <select
                className="block w-full px-3 py-2.5 bg-white dark:bg-midnight-900/50 border border-slate-300 dark:border-white/10 rounded-lg text-slate-900 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-brand-400/50 focus:border-brand-400/50 transition-all text-sm"
                value={selectedAreaCoach}
                onChange={(e) => handleAreaCoachChange(e.target.value)}
              >
                <option value="">All Area Coaches</option>
                {areaCoaches.map((areaCoach) => (
                  <option key={areaCoach.id} value={areaCoach.id}>
                    {areaCoach.name}
                  </option>
                ))}
              </select>
            </div>
          </>
        )}
      </div>

      {/* Status and Date Range */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
            Status
          </label>
          <select
            className="block w-full px-3 py-2.5 bg-white dark:bg-midnight-900/50 border border-slate-300 dark:border-white/10 rounded-lg text-slate-900 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-brand-400/50 focus:border-brand-400/50 transition-all text-sm"
            value={filters.status || 2}
            onChange={(e) => handleFilterChange('status', Number(e.target.value))}
          >
            <option value={0}>PENDING</option>
            <option value={1}>COMPLETED</option>
            <option value={2}>ALL</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
            From Date
          </label>
          <input
            type="date"
            className="block w-full px-3 py-2.5 bg-white dark:bg-midnight-900/50 border border-slate-300 dark:border-white/10 rounded-lg text-slate-900 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-brand-400/50 focus:border-brand-400/50 transition-all text-sm"
            value={filters.date_from || ''}
            onChange={(e) => handleFilterChange('date_from', e.target.value)}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
            To Date
          </label>
          <input
            type="date"
            className="block w-full px-3 py-2.5 bg-white dark:bg-midnight-900/50 border border-slate-300 dark:border-white/10 rounded-lg text-slate-900 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-brand-400/50 focus:border-brand-400/50 transition-all text-sm"
            value={filters.date_to || ''}
            onChange={(e) => handleFilterChange('date_to', e.target.value)}
          />
        </div>
      </div>
    </div>
  );

  // Render card view
  const renderCardView = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {requisitions.map((requisition) => {
        const status = getCompletionStatus(requisition);
        return (
          <div
            key={requisition.id}
            className="bg-white dark:bg-midnight-800/40 border border-slate-200 dark:border-white/5 rounded-xl p-4 hover:shadow-lg dark:hover:shadow-2xl transition-all flex flex-col"
          >
            {/* Status Badge */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 flex-wrap">
                {status === 'COMPLETED' ? (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/30">
                    <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    COMPLETED
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-500/30">
                    <svg className="w-3 h-3 mr-1 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                        clipRule="evenodd"
                      />
                    </svg>
                    PENDING
                  </span>
                )}
                
                {/* Jobcard Badges */}
                {requisition.order?.jobcards && requisition.order.jobcards.length > 0 ? (
                  <div className="flex items-center gap-1 flex-wrap">
                    <svg className="w-3 h-3 text-slate-600 dark:text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    {requisition.order.jobcards.map((jobcard) => (
                      status === 'COMPLETED' ? (
                        <a
                          key={jobcard.id}
                          href={`https://api.colefaxjobcards.co.za/api/jobcards/${jobcard.id}/print`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-cyan-100 dark:bg-cyan-500/10 text-cyan-700 dark:text-cyan-400 hover:bg-cyan-200 dark:hover:bg-cyan-500/20 transition-colors"
                        >
                          Job Card #{jobcard.job_number}
                        </a>
                      ) : (
                        <span
                          key={jobcard.id}
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 dark:bg-slate-700/50 text-slate-600 dark:text-slate-400 cursor-not-allowed opacity-75"
                          title="Jobcard download only available for completed requisitions"
                        >
                          Job Card #{jobcard.job_number}
                        </span>
                      )
                    ))}
                  </div>
                ) : (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-bold bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400 border-2 border-red-300 dark:border-red-500/50 shadow-sm animate-pulse">
                    <svg className="w-3.5 h-3.5 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    No Jobcard
                  </span>
                )}
              </div>
              <span className="text-xs text-slate-500 dark:text-slate-400">{requisition.date}</span>
            </div>

            {/* Order Number */}
            {requisition.order && (
              <div className="mb-2">
                <a
                  href={`https://colefaxpo.co.za/livewire/update/${requisition.order.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 font-semibold text-sm"
                >
                  Order #{requisition.order.order_number || requisition.order.reference_number}
                </a>
              </div>
            )}

            {/* Store */}
            <div className="mb-2">
              <span className="text-xs font-medium text-slate-600 dark:text-slate-400">Store: </span>
              <div className="flex flex-wrap gap-1 mt-1">
                {requisition.stores.map((store) => (
                  <span
                    key={store.id}
                    className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 dark:bg-slate-700/50 text-slate-700 dark:text-slate-300"
                  >
                    {store.name}
                  </span>
                ))}
              </div>
            </div>

            {/* Category */}
            {requisition.category && (
              <div className="mb-2">
                <span className="text-xs font-medium text-slate-600 dark:text-slate-400">Category: </span>
                <div className="flex flex-wrap gap-1 mt-1">
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-400">
                    {requisition.category.name}
                  </span>
                  {requisition.subcategory && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 dark:bg-purple-500/10 text-purple-700 dark:text-purple-400">
                      {requisition.subcategory.name}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Supplier */}
            <div className="mb-3">
              <span className="text-xs font-medium text-slate-600 dark:text-slate-400">Supplier/Technician: </span>
              <span className="text-sm text-slate-900 dark:text-slate-200">
                {requisition.internal === 1
                  ? requisition.technician?.name
                  : requisition.supplier?.name}
              </span>
            </div>

            {/* Action Button */}
            <button
              onClick={() => handleViewDetails(requisition)}
              className="mt-auto w-full inline-flex items-center justify-center px-4 py-2 bg-brand-600 dark:bg-brand-500/10 hover:bg-brand-700 dark:hover:bg-brand-500/20 text-white dark:text-brand-400 border-0 dark:border dark:border-brand-500/30 rounded-lg font-medium transition-all text-sm"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                />
              </svg>
              View Details
            </button>
          </div>
        );
      })}
    </div>
  );

  // Render table view
  const renderTableView = () => (
    <div className="bg-white dark:bg-midnight-800/40 border border-slate-200 dark:border-white/5 rounded-xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-200 dark:divide-white/5">
          <thead className="bg-slate-50 dark:bg-midnight-900/50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                Order #
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                Jobcards
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                Store
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                Category
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                Supplier
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-midnight-800/40 divide-y divide-slate-200 dark:divide-white/5">
            {requisitions.map((requisition) => {
              const status = getCompletionStatus(requisition);
              return (
                <tr
                  key={requisition.id}
                  className="hover:bg-slate-50 dark:hover:bg-white/5 transition-colors"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    {status === 'COMPLETED' ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/30">
                        <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clipRule="evenodd"
                          />
                        </svg>
                        COMPLETED
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-500/30">
                        <svg className="w-3 h-3 mr-1 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                            clipRule="evenodd"
                          />
                        </svg>
                        PENDING
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {requisition.order && (
                      <a
                        href={`https://colefaxpo.co.za/livewire/update/${requisition.order.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 font-medium text-sm"
                      >
                        #{requisition.order.order_number || requisition.order.reference_number}
                      </a>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {requisition.order?.jobcards && requisition.order.jobcards.length > 0 ? (
                      <div className="flex items-center gap-1 flex-wrap">
                        <svg className="w-3 h-3 text-slate-600 dark:text-slate-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                        </svg>
                        <span className="text-xs text-slate-600 dark:text-slate-400">
                          {requisition.order.jobcards.length} {requisition.order.jobcards.length > 1 ? 'Jobcards' : 'Jobcard'}
                        </span>
                        <div className="flex gap-1 flex-wrap">
                          {requisition.order.jobcards.map((jobcard) => (
                            status === 'COMPLETED' ? (
                              <a
                                key={jobcard.id}
                                href={`https://api.colefaxjobcards.co.za/api/jobcards/${jobcard.id}/print`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-cyan-100 dark:bg-cyan-500/10 text-cyan-700 dark:text-cyan-400 hover:bg-cyan-200 dark:hover:bg-cyan-500/20 transition-colors"
                              >
                                #{jobcard.job_number}
                              </a>
                            ) : (
                              <span
                                key={jobcard.id}
                                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 dark:bg-slate-700/50 text-slate-600 dark:text-slate-400 cursor-not-allowed opacity-75"
                                title="Jobcard download only available for completed requisitions"
                              >
                                #{jobcard.job_number}
                              </span>
                            )
                          ))}
                        </div>
                      </div>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-bold bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400 border-2 border-red-300 dark:border-red-500/50 shadow-sm animate-pulse">
                        <svg className="w-3.5 h-3.5 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        No Jobcard
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-700 dark:text-slate-300">
                    {requisition.date}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-wrap gap-1">
                      {requisition.stores.map((store) => (
                        <span
                          key={store.id}
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 dark:bg-slate-700/50 text-slate-700 dark:text-slate-300"
                        >
                          {store.name}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col gap-1">
                      {requisition.category && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-400">
                          {requisition.category.name}
                        </span>
                      )}
                      {requisition.subcategory && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 dark:bg-purple-500/10 text-purple-700 dark:text-purple-400">
                          {requisition.subcategory.name}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-700 dark:text-slate-300">
                    {requisition.internal === 1
                      ? requisition.technician?.name
                      : requisition.supplier?.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <button
                      onClick={() => handleViewDetails(requisition)}
                      className="inline-flex items-center px-3 py-1.5 bg-brand-600 dark:bg-brand-500/10 hover:bg-brand-700 dark:hover:bg-brand-500/20 text-white dark:text-brand-400 border-0 dark:border dark:border-brand-500/30 rounded-lg font-medium transition-all"
                    >
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                      View
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-brand-400 to-accent-400 bg-clip-text text-transparent">
            Maintenance Log
          </h1>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            Track and manage equipment maintenance records
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleExport}
            className="inline-flex items-center gap-2 px-3 md:px-4 py-2 bg-emerald-600 dark:bg-emerald-500/10 hover:bg-emerald-700 dark:hover:bg-emerald-500/20 text-white dark:text-emerald-400 border-0 dark:border dark:border-emerald-500/30 rounded-lg font-medium transition-all text-sm"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <span className="hidden sm:inline">Export</span>
          </button>
          <button
            onClick={clearFilters}
            className="inline-flex items-center gap-2 px-3 md:px-4 py-2 bg-slate-200 dark:bg-slate-700/50 hover:bg-slate-300 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 border border-slate-300 dark:border-slate-600/50 rounded-lg font-medium transition-all text-sm"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            <span className="hidden sm:inline">Clear Filters</span>
          </button>
        </div>
      </div>

      {/* Mobile Filter Toggle */}
      <div className="md:hidden">
        <button
          onClick={() => setMobileFiltersVisible(!mobileFiltersVisible)}
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-white dark:bg-midnight-800/40 border border-slate-300 dark:border-white/10 rounded-lg text-slate-700 dark:text-slate-300 font-medium transition-all"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
            />
          </svg>
          Filters
          {mobileFiltersVisible ? (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
            </svg>
          ) : (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          )}
        </button>
      </div>

      {/* Mobile Filters Drawer */}
      {mobileFiltersVisible && (
        <div className="md:hidden bg-white dark:bg-midnight-800/40 border border-slate-200 dark:border-white/5 rounded-xl p-4">
          {renderFilters()}
        </div>
      )}

      {/* Desktop Filter Toggle */}
      <div className="hidden md:block">
        <button
          onClick={() => setDesktopFiltersVisible(!desktopFiltersVisible)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-midnight-800/40 border border-slate-300 dark:border-white/10 rounded-lg text-slate-700 dark:text-slate-300 font-medium transition-all text-sm hover:bg-slate-50 dark:hover:bg-midnight-800/60"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
            />
          </svg>
          {desktopFiltersVisible ? 'Hide Filters' : 'Show Filters'}
        </button>
      </div>

      {/* Desktop Filters */}
      {desktopFiltersVisible && (
        <div className="hidden md:block bg-white dark:bg-midnight-800/40 border border-slate-200 dark:border-white/5 rounded-xl p-6">
          {renderFilters()}
        </div>
      )}

      {/* Date Range Info & View Toggle */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-sm">
        <div className="text-slate-600 dark:text-slate-400">
          Showing records from{' '}
          <span className="text-brand-600 dark:text-brand-400 font-semibold">{filters.date_from}</span> to{' '}
          <span className="text-brand-600 dark:text-brand-400 font-semibold">{filters.date_to}</span>
          <span className="mx-2">•</span>
          Page {pagination.current_page} of {pagination.last_page} ({pagination.total} total)
        </div>
        <div className="flex gap-1 bg-slate-100 dark:bg-midnight-900/50 p-1 rounded-lg">
          <button
            onClick={() => setViewMode('card')}
            className={`px-3 py-1.5 rounded-md font-medium transition-all text-sm ${
              viewMode === 'card'
                ? 'bg-white dark:bg-midnight-800 text-brand-600 dark:text-brand-400 shadow-sm'
                : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
              />
            </svg>
          </button>
          <button
            onClick={() => setViewMode('table')}
            className={`px-3 py-1.5 rounded-md font-medium transition-all text-sm ${
              viewMode === 'table'
                ? 'bg-white dark:bg-midnight-800 text-brand-600 dark:text-brand-400 shadow-sm'
                : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-400"></div>
        </div>
      ) : requisitions.length > 0 ? (
        viewMode === 'card' ? (
          renderCardView()
        ) : (
          <div className="hidden md:block">{renderTableView()}</div>
        )
      ) : (
        <div className="text-center py-12 bg-white dark:bg-midnight-800/40 border border-slate-200 dark:border-white/5 rounded-xl">
          <svg
            className="mx-auto h-12 w-12 text-slate-400 dark:text-slate-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">No maintenance records found</p>
        </div>
      )}

      {/* Pagination */}
      {pagination.last_page > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => fetchMaintenanceLog(pagination.current_page - 1)}
            disabled={pagination.current_page === 1}
            className="inline-flex items-center px-3 md:px-4 py-2 bg-white dark:bg-slate-700/50 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 border border-slate-300 dark:border-slate-600/50 rounded-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span className="hidden sm:inline">Previous</span>
          </button>
          <span className="text-sm text-slate-600 dark:text-slate-400">
            Page {pagination.current_page} of {pagination.last_page}
          </span>
          <button
            onClick={() => fetchMaintenanceLog(pagination.current_page + 1)}
            disabled={pagination.current_page === pagination.last_page}
            className="inline-flex items-center px-3 md:px-4 py-2 bg-white dark:bg-slate-700/50 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 border border-slate-300 dark:border-slate-600/50 rounded-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm"
          >
            <span className="hidden sm:inline">Next</span>
            <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      )}

      {/* Maintenance Details Modal */}
      <MaintenanceDetailsModal
        requisition={selectedRequisition}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </div>
  );
};

export default MaintenanceLogPage;
